import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import PanelContent from "../../lib/panel/panel-content";
import { cn } from "@babackapp/ui/lib/utils";
import { Button } from "@babackapp/ui/components/button";
import { Trash2Icon, UploadCloudIcon } from "lucide-react";
import { useEditor } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import { useUnavailableVariableIds } from "@babackapp/variables-registry";
import createVariablesMentionExtension, {
  parseMentionsFromText,
} from "../../lib/mention-extension";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Block } from "../blocks-types";

export const DEFAULT_BANNER = {
  href: "",
  imageUrl:
    "https://via.assets.so/img.jpg?w=600&h=300&bg=e5e7eb&text=No+image&f=png",
  imageId: null,
  imageUploadKey: null,
};

export interface BannerModalSavePayload {
  href: string;
  imageFile: File | null;
  removeImage: boolean;
}

export function EmailPanelImage() {
  const blockProps = useBlockPanelPropsContext();
  const { formAPI } = blockProps;
  const block: Block<"banner"> = blockProps.block as Block<"banner">;

  const item = block.properties?.items?.[0] ?? DEFAULT_BANNER;
  const unavailableVariableIds = useUnavailableVariableIds();

  const [imageUrl, setImageUrl] = useState<string>(
    item.imageId
      ? `https://baback-banner-images.s3.eu-west-3.amazonaws.com/${item.imageId}`
      : (item.imageUrl ?? DEFAULT_BANNER.imageUrl),
  );

  // Sync state with props when imageId changes (after successful upload)
  useEffect(() => {
    if (item.imageId) {
      const s3Url = `https://baback-banner-images.s3.eu-west-3.amazonaws.com/${item.imageId}`;
      setImageUrl(s3Url);
    } else if (item.imageUrl) {
      setImageUrl(item.imageUrl);
    }
  }, [item.imageId, item.imageUrl]);

  const handleBannerSave = (values: BannerModalSavePayload) => {
    const imageUploadKey = item.imageUploadKey ?? crypto.randomUUID();

    if (values.imageFile) {
      setImageUrl(URL.createObjectURL(values.imageFile));
    }

    if (values.removeImage) {
      setImageUrl(DEFAULT_BANNER.imageUrl);
      // Remove file from uploads and clear banner properties
      formAPI.registerImage(imageUploadKey, null);
      return formAPI.updateField(`${formAPI.blockPath}.properties`, {
        items: [],
      });
    }

    // Add file to uploads registry and update banner properties
    formAPI.registerImage(imageUploadKey, values.imageFile);
    formAPI.updateField(`${formAPI.blockPath}.properties.items.0`, {
      href: values.href,
      // craftsman-ignore: TS003
      imageUrl: values.imageFile
        ? URL.createObjectURL(values.imageFile!)
        : null,
      imageId: null,
      imageUploadKey: imageUploadKey,
    });
  };
  const hrefEditor = useEditor({
    extensions: [
      Document,
      createVariablesMentionExtension(blockProps.variables, unavailableVariableIds),
      Paragraph,
      Text,
    ],
    content: parseMentionsFromText(item.href ?? "", blockProps.variables),
    editorProps: {
      attributes: {
        class:
          "min-h-9 px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0",
        style: "white-space: pre-wrap;",
      },
    },
  });

  useEffect(() => {
    if (!hrefEditor) return;
    const doc = parseMentionsFromText(item.href ?? "", blockProps.variables);
    if (
      hrefEditor.getJSON &&
      JSON.stringify(hrefEditor.getJSON()) !== JSON.stringify(doc)
    ) {
      hrefEditor.commands.setContent(doc, { emitUpdate: false });
    }
  }, [hrefEditor, item.href, open, blockProps.variables]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    imageUrl && imageUrl !== DEFAULT_BANNER.imageUrl ? imageUrl : null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    },
    [],
  );

  const applyFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please choose an image file.");
      return;
    }
    setErrorMessage(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setPreviewUrl(url);
    handleBannerSave?.({
      href: item.href ?? "",
      imageFile: file,
      removeImage: false,
    });
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      const [file] = Array.from(event.dataTransfer.files ?? []);
      if (file) {
        applyFile(file);
      }
    },
    [applyFile],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const [file] = Array.from(event.target.files ?? []);
      if (file) {
        applyFile(file);
      }
      // Allow selecting the same file again
      event.target.value = "";
    },
    [applyFile],
  );

  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleChooseFile();
      }
    },
    [handleChooseFile],
  );

  const handleRemoveImage = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);
    setErrorMessage(null);
    handleBannerSave?.({
      href: "",
      imageFile: null,
      removeImage: true,
    });
  }, []);

  return (
    <PanelContent title="Image">
      <div className="space-y-2 w-full">
        <div
          role="button"
          tabIndex={0}
          onClick={handleChooseFile}
          onKeyDown={handleKeyDown}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            "group relative flex min-h-[8rem] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed bg-muted/40 text-center text-sm text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            isDragging && "border-primary bg-primary/5 text-primary",
            previewUrl &&
              "border border-solid border-muted-foreground/25 bg-background p-2",
          )}
        >
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            className="hidden"
          />
          {previewUrl ? (
            <div className="relative w-full">
              <img
                src={previewUrl}
                alt="Selected banner preview"
                className="h-40 w-full rounded-md object-cover"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveImage();
                }}
                className="absolute right-3 top-3"
              >
                <Trash2Icon className="mr-1 size-4" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <UploadCloudIcon className="size-8" />
              <div>
                <p className="font-medium">Drop an image here</p>
                <p className="text-sm text-muted-foreground">
                  or click to select a file
                </p>
              </div>
            </div>
          )}
        </div>
        {errorMessage ? (
          <p className="text-sm text-destructive">{errorMessage}</p>
        ) : null}
      </div>
    </PanelContent>
  );
}
